import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/utils/api';
import Layout from '@/components/Layout';
import { EVENTO_VER_TOUR } from '@/components/TourBienvenida';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlertTriangle, Loader2, Mail, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const SUPPORT_EMAIL = 'ejerciciofisicoysaludarg@gmail.com';

export default function SettingsPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleConfirmCancel = async () => {
    setSubmitting(true);
    try {
      const res = await api.cancelAccount();
      toast.success(
        res?.message ||
          'Tu solicitud de baja fue registrada. Te enviamos un email de confirmación.'
      );
      setConfirmOpen(false);
      // Logout inmediato para que la sesión termine al instante.
      await logout();
      navigate('/');
    } catch (e) {
      toast.error(e?.message || 'No se pudo registrar la solicitud de baja.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="settings-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">
            Configuración
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Administrá tu cuenta de EVALPRO.
          </p>
          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={() => window.dispatchEvent(new CustomEvent(EVENTO_VER_TOUR))}
            data-testid="ver-tour"
          >
            Ver el tour de nuevo
          </Button>
        </div>

        {/* Datos de la cuenta */}
        <Card className="border-slate-200" data-testid="settings-account-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-slate-500" />
              Datos de la cuenta
            </CardTitle>
            <CardDescription>Información asociada a tu sesión.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between border-b border-slate-100 py-2">
              <span className="text-slate-500">Nombre</span>
              <span className="font-medium text-slate-800">{user?.name || '—'}</span>
            </div>
            <div className="flex justify-between border-b border-slate-100 py-2">
              <span className="text-slate-500">Email</span>
              <span className="font-medium text-slate-800">{user?.email || '—'}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-slate-500">Tipo de cuenta</span>
              <span className="font-medium text-slate-800 capitalize">
                {user?.account_type || '—'}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Zona de peligro */}
        <Card
          className="border-red-200 bg-red-50/30"
          data-testid="settings-danger-card"
        >
          <CardHeader>
            <CardTitle className="text-base text-red-700 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Cancelar cuenta
            </CardTitle>
            <CardDescription className="text-red-900/80">
              Al solicitar la baja, tu cuenta queda marcada como{' '}
              <strong>pendiente de baja</strong>. Tus datos personales y los de tus
              alumnos serán eliminados en un plazo no mayor a{' '}
              <strong>30 días corridos</strong>. Los datos de facturación se
              conservan según obligación fiscal.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-sm text-slate-700 bg-white border border-slate-200 rounded-lg p-3 flex items-start gap-2">
              <Mail className="h-4 w-4 text-slate-500 flex-shrink-0 mt-0.5" />
              <span>
                También podés solicitar la baja escribiendo a{' '}
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="underline font-medium text-[#1F3A5F]"
                  data-testid="settings-support-email-link"
                >
                  {SUPPORT_EMAIL}
                </a>
                .
              </span>
            </div>

            <Button
              variant="destructive"
              onClick={() => setConfirmOpen(true)}
              className="bg-red-600 hover:bg-red-700"
              data-testid="cancel-account-button"
            >
              Solicitar baja de cuenta
            </Button>
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="cancel-account-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Confirmás la baja de tu cuenta?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                Esta acción marcará tu cuenta como pendiente de baja. Tus datos
                personales y los de tus alumnos serán <strong>eliminados</strong>{' '}
                en un plazo no mayor a 30 días corridos.
              </span>
              <span className="block">
                Los datos de facturación se conservan según obligación fiscal. Si
                cambiás de opinión dentro de ese plazo, podés escribirnos a{' '}
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="underline text-[#1F3A5F]"
                >
                  {SUPPORT_EMAIL}
                </a>
                .
              </span>
              <span className="block text-xs text-slate-500">
                Recibirás un email de confirmación a la dirección registrada.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={submitting}
              data-testid="cancel-account-dialog-cancel"
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmCancel}
              disabled={submitting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="cancel-account-dialog-confirm"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Procesando...
                </>
              ) : (
                'Sí, dar de baja'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
